import { axiosClient } from '@/shared/apis/config';
import {
  DtoContentType,
  DtoFolderItemType,
  DtoExtension,
  DtoContentStatus,
  DtoStatus,
  DtoProjectStatus,
  DtoProjectType,
  DtoProjectErrorType,
} from '@/shared/constants';
import { ApiResponse } from '@/shared/types/api/response';
import { CustomQueryOptions } from '@/shared/types/global/react-query/query';
import { contentAiProcessingStatusSchema } from './content-ai-processing-status';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';

export const getFolderItemsRequestSchema = z.object({
  id: z.number(),
  project: z.boolean().optional(),
  contentTypeList: z.array(z.nativeEnum(DtoContentType)).optional(),
  sort: z.string().optional(),
});

// baseItemSchema에서는 type 필드를 제외합니다
const baseItemSchema = z.object({
  name: z.string(),
  id: z.number(),
  updatedAt: z.string().date(),
  createdAt: z.string().date(),
  itemId: z.number(),
  duration: z.number().optional(),
});

// 재귀적 참조를 위한 전방 선언
// z.lazy는 Zod에서 재귀적 타입을 정의할 때 사용합니다
// 먼저 빈 객체로 선언하고 나중에 실제 값을 할당합니다
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type FolderItemsResponseSchemaType = z.ZodType<any>;
// eslint-disable-next-line prefer-const
let folderItemsResponseSchema: FolderItemsResponseSchemaType;

const getFolderItemsContentResponseSchema = baseItemSchema.extend({
  type: z.literal(DtoFolderItemType.content),
  publicUrl: z.string().optional(),
  thumbnailPublicUrl: z.string().optional(),
  contentType: z.nativeEnum(DtoContentType),
  extension: z.nativeEnum(DtoExtension),
  status: z.nativeEnum(DtoContentStatus),
  indexingStatus: contentAiProcessingStatusSchema,
  embeddingStatus: contentAiProcessingStatusSchema,
});

const getFolderItemsFolderResponseSchema = baseItemSchema.extend({
  type: z.literal(DtoFolderItemType.folder),
  folderType: z.number(),
  systemFolder: z.boolean(),
  childLastUpdatedAt: z.string().date(),
  childItemList: z.lazy(() => z.array(folderItemsResponseSchema)),
});

const folderItemProjectBaseVideoDtoSchema = z.object({
  contentId: z.number(),
  publicUrl: z.string(),
  thumbnailPublicUrl: z.string().optional(),
  duration: z.number(),
  inPoint: z.number(),
  outPoint: z.number(),
  status: z.nativeEnum(DtoStatus),
});

const statusProgressSchema = z.object({
  current: z.number(),
  total: z.number(),
  label: z.string(),
  statusKey: z.string(),
});

const getFolderItemsProjectResponseSchema = baseItemSchema.extend({
  type: z.literal(DtoFolderItemType.project),
  status: z.nativeEnum(DtoProjectStatus),
  publicUrl: z.string().optional(),
  duration: z.number(),
  childCount: z.number(),
  baseVideoList: z.array(folderItemProjectBaseVideoDtoSchema),
  creationStepCurrentIndex: z.number().nullable(),
  creationStepTotalCount: z.number(),
  projectType: z.nativeEnum(DtoProjectType),
  recipeKey: z.string().nullable().optional(),
  thumbnailUrl: z.string().nullable().optional(),
  thumbnailMediaType: z.enum(['image', 'video']).nullable().optional(),
  errorCode: z.nativeEnum(DtoProjectErrorType).nullable(),
  statusProgress: statusProgressSchema.nullable().optional(),
  isRetryable: z.boolean().default(false),
  failedStep: z.string().nullable().default(null),
  retryCount: z.number().default(0),
  creatorAccountId: z.number().nullable().optional(),
});

// 이제 재귀적 참조를 포함한 최종 타입을 정의합니다
folderItemsResponseSchema = z.discriminatedUnion('type', [
  getFolderItemsContentResponseSchema,
  getFolderItemsFolderResponseSchema,
  getFolderItemsProjectResponseSchema,
]);

export type GetFolderItemsRequestType = z.infer<
  typeof getFolderItemsRequestSchema
>;
export type GetFolderItemsContentResponseType = z.infer<
  typeof getFolderItemsContentResponseSchema
>;
export type GetFolderItemsFolderResponseType = z.infer<
  typeof getFolderItemsFolderResponseSchema
>;
export type GetFolderItemsProjectResponseType = z.infer<
  typeof getFolderItemsProjectResponseSchema
>;

// 모든 응답 타입을 하나로 통합한 타입
export type FolderItemsResponseType = z.infer<typeof folderItemsResponseSchema>;

// 타입 가드 함수
export const isContentItem = (
  item: FolderItemsResponseType,
): item is GetFolderItemsContentResponseType => Number(item.type) === 1;

export const isFolderItem = (
  item: FolderItemsResponseType,
): item is GetFolderItemsFolderResponseType => Number(item.type) === 0;

export const isProjectItem = (
  item: FolderItemsResponseType,
): item is GetFolderItemsProjectResponseType => Number(item.type) === 2;

/**
 * 폴더 아이템 응답 타입
 */
export type FolderItemsResponseUnion = FolderItemsResponseType;

/**
 * 폴더 아이템 목록 API 응답 타입
 */
type GetFolderItemsResponse = ApiResponse<FolderItemsResponseUnion[]>;

/**
 * 폴더 아이템 목록을 가져오는 API 함수
 *
 * @param id - 폴더 ID
 * @param project - 프로젝트 필터 (선택적)
 * @param contentTypeList - 콘텐츠 타입 필터 (선택적)
 * @param sort - 정렬 옵션 (선택적)
 * @returns 폴더 아이템 목록 응답
 */
export const getFolderItemsApi = async ({
  id,
  project,
  contentTypeList,
  sort,
}: GetFolderItemsRequestType) => {
  const response = await axiosClient.get<GetFolderItemsResponse>(
    `/v1.0/folder/${id}/items`,
    {
      params: {
        project,
        contentTypeList,
        sort,
      },
    },
  );

  return response.data as GetFolderItemsResponse;
};

/**
 * 폴더 아이템 목록 쿼리 키
 */
export const QueryKeyUseGetFolderItemsApi = 'folderItems' as const;

/**
 * 폴더 아이템 목록을 가져오는 React Query 훅
 *
 * @param requestParams - 요청 파라미터 (폴더 ID, 필터 등)
 * @param options - React Query 옵션
 * @returns 폴더 아이템 목록 쿼리 결과
 */
export const useGetFolderItemsApi = (
  { id, project, contentTypeList, sort }: GetFolderItemsRequestType,
  options?: CustomQueryOptions<GetFolderItemsResponse>,
) => {
  const { queryKey, ...rest } = options ?? {};

  return useQuery({
    queryKey: [
      QueryKeyUseGetFolderItemsApi,
      id,
      project ?? null,
      contentTypeList?.join(',') ?? null,
      sort,
      ...(queryKey ?? []),
    ],
    queryFn: () => getFolderItemsApi({ id, project, contentTypeList, sort }),
    ...rest,
  });
};
